import { useEffect, useMemo, useState } from 'react';
import { useViewport } from '@xyflow/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchIncomingFlowchartSubprocessLinks } from '../../request/flowchart/subprocessLinks.api';
import { NODE_HEIGHT, NODE_WIDTH } from '../../features/flowchart/flowchartDimensions.ts';

const PILL_WIDTH = 30;
const LINE_GAP = 4;

function processDisplayId(link) {
  return String(
    link?.target_process_display_id ||
      link?.targetProcessDisplayId ||
      link?.target_process_id ||
      link?.targetProcessId ||
      ''
  ).trim();
}

function sourceProcessDisplayId(link) {
  return String(
    link?.source_process_display_id ||
      link?.sourceProcessDisplayId ||
      link?.source_process_id ||
      link?.sourceProcessId ||
      ''
  ).trim();
}

function nodeSize(node) {
  return {
    width: Number(node?.measured?.width || node?.width || NODE_WIDTH) || NODE_WIDTH,
    height: Number(node?.measured?.height || node?.height || NODE_HEIGHT) || NODE_HEIGHT,
  };
}

function outgoingAnchor(node) {
  const x = Number(node?.position?.x) || 0;
  const y = Number(node?.position?.y) || 0;
  const { width, height } = nodeSize(node);
  return {
    x: x + width / 2,
    y: y + height,
  };
}

function incomingAnchor(node) {
  const x = Number(node?.position?.x) || 0;
  const y = Number(node?.position?.y) || 0;
  const { width } = nodeSize(node);
  return {
    x: x + width / 2,
    y,
  };
}

function pillHeight(processId) {
  return Math.max(78, String(processId || '').length * 7.2 + 24);
}

function outgoingBadgePosition(anchor, index, total, height) {
  const spread = Math.max(1, total);
  const offsetX = (index - (spread - 1) / 2) * 44;
  return {
    x: anchor.x + offsetX,
    y: anchor.y + 18 + height / 2,
    height,
  };
}

function incomingBadgePosition(anchor, index, total, height) {
  const spread = Math.max(1, total);
  const offsetX = (index - (spread - 1) / 2) * 44;
  return {
    x: anchor.x + offsetX,
    y: anchor.y - 18 - height / 2,
    height,
  };
}

function ProcessCapsule({
  processId,
  anchor,
  badge,
  itemKey,
  gradientId,
  glowId,
  stroke,
  lineStroke,
  fromAbove,
  onClick,
}) {
  const topY = badge.y - badge.height / 2;
  const bottomY = badge.y + badge.height / 2;
  const lineNodeY = fromAbove ? anchor.y : anchor.y;
  const lineBadgeY = fromAbove ? bottomY - 2 : topY + 2;
  return (
    <g
      onClick={onClick}
      style={{ pointerEvents: 'all', cursor: 'pointer' }}
      aria-label="Open end-to-end flowchart"
    >
      {[-LINE_GAP, 0, LINE_GAP].map((offset) => (
        <line
          key={`${itemKey}-line-${offset}`}
          x1={anchor.x + offset}
          y1={lineNodeY}
          x2={badge.x + offset}
          y2={lineBadgeY}
          stroke={lineStroke}
          strokeWidth={1.35}
          strokeLinecap="round"
          strokeDasharray="3 4"
          opacity={0.85}
        />
      ))}
      <rect
        x={badge.x - PILL_WIDTH / 2}
        y={topY}
        width={PILL_WIDTH}
        height={badge.height}
        rx={PILL_WIDTH / 2}
        ry={PILL_WIDTH / 2}
        fill={`url(#${gradientId})`}
        stroke={stroke}
        strokeWidth={1.6}
        filter={`url(#${glowId})`}
      />
      <rect
        x={badge.x - PILL_WIDTH / 2 + 3}
        y={topY + 3}
        width={PILL_WIDTH - 6}
        height={badge.height - 6}
        rx={(PILL_WIDTH - 6) / 2}
        ry={(PILL_WIDTH - 6) / 2}
        fill="none"
        stroke="rgba(255,255,255,0.35)"
        strokeWidth={1}
      />
      <text
        x={badge.x}
        y={badge.y}
        textAnchor="middle"
        dominantBaseline="middle"
        transform={`rotate(90 ${badge.x} ${badge.y})`}
        fill="#ffffff"
        fontSize="10"
        fontWeight="700"
        letterSpacing="0.8"
      >
        {processId}
      </text>
    </g>
  );
}

export default function FlowchartSubprocessLinkOverlay({
  links = [],
  nodes = [],
  processUuid = '',
}) {
  const viewport = useViewport();
  const location = useLocation();
  const navigate = useNavigate();
  const [incomingLinks, setIncomingLinks] = useState([]);
  const endToEndPath = useMemo(() => {
    const parts = String(location?.pathname || '').split('/').filter(Boolean);
    const cycleIndex = parts.findIndex((part) => part === 'sox-cycles');
    const cycleId = cycleIndex >= 0 ? parts[cycleIndex + 1] : '';
    return cycleId ? `/sox-cycles/${cycleId}/end-to-end-flowchart` : '/sox-cycles';
  }, [location?.pathname]);

  const handleCapsuleClick = useMemo(
    () => () => {
      navigate(endToEndPath);
    },
    [navigate, endToEndPath]
  );

  useEffect(() => {
    let cancelled = false;
    const targetProcessId = String(processUuid || '').trim();
    if (!targetProcessId) {
      setIncomingLinks([]);
      return undefined;
    }
    fetchIncomingFlowchartSubprocessLinks(targetProcessId)
      .then((rows) => {
        if (!cancelled) setIncomingLinks(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setIncomingLinks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [processUuid]);

  const nodeById = useMemo(() => {
    const map = new Map();
    (Array.isArray(nodes) ? nodes : [])
      .filter((node) => node?.type !== 'swimlane')
      .forEach((node) => {
        if (node?.id) map.set(String(node.id), node);
      });
    return map;
  }, [nodes]);

  const groupedOutgoing = useMemo(() => {
    const groups = new Map();
    (Array.isArray(links) ? links : []).forEach((link) => {
      const sourceId = String(link?.source_node_id || link?.sourceNodeId || '');
      const targetProcessId = String(link?.target_process_id || link?.targetProcessId || '');
      if (!sourceId || !targetProcessId) return;
      if (!groups.has(sourceId)) groups.set(sourceId, []);
      const sourceLinks = groups.get(sourceId);
      const alreadyShown = sourceLinks.some(
        (existing) =>
          String(existing?.target_process_id || existing?.targetProcessId || '') === targetProcessId
      );
      if (!alreadyShown) sourceLinks.push(link);
    });
    return groups;
  }, [links]);

  const groupedIncoming = useMemo(() => {
    const groups = new Map();
    (Array.isArray(incomingLinks) ? incomingLinks : []).forEach((link) => {
      const targetNodeId = String(link?.target_node_id || link?.targetNodeId || '');
      const sourceProcessId = String(link?.source_process_id || link?.sourceProcessId || '');
      if (!targetNodeId || !sourceProcessId) return;
      if (!groups.has(targetNodeId)) groups.set(targetNodeId, []);
      const nodeLinks = groups.get(targetNodeId);
      const alreadyShown = nodeLinks.some(
        (existing) =>
          String(existing?.source_process_id || existing?.sourceProcessId || '') === sourceProcessId
      );
      if (!alreadyShown) nodeLinks.push(link);
    });
    return groups;
  }, [incomingLinks]);

  const outgoingDrawings = useMemo(() => {
    const items = [];
    groupedOutgoing.forEach((sourceLinks, sourceId) => {
      const node = nodeById.get(sourceId);
      if (!node) return;
      const anchor = outgoingAnchor(node);
      sourceLinks.forEach((link, index) => {
        const processId = processDisplayId(link);
        const height = pillHeight(processId);
        const badge = outgoingBadgePosition(anchor, index, sourceLinks.length, height);
        items.push({
          processId,
          anchor,
          badge,
          key: `out-${sourceId}-${link.target_process_id || index}`,
        });
      });
    });
    return items;
  }, [groupedOutgoing, nodeById]);

  const incomingDrawings = useMemo(() => {
    const items = [];
    groupedIncoming.forEach((nodeLinks, targetNodeId) => {
      const node = nodeById.get(targetNodeId);
      if (!node) return;
      const anchor = incomingAnchor(node);
      nodeLinks.forEach((link, index) => {
        const processId = sourceProcessDisplayId(link);
        const height = pillHeight(processId);
        const badge = incomingBadgePosition(anchor, index, nodeLinks.length, height);
        items.push({
          processId,
          anchor,
          badge,
          key: `in-${targetNodeId}-${link.source_process_id || index}`,
        });
      });
    });
    return items;
  }, [groupedIncoming, nodeById]);

  if (outgoingDrawings.length === 0 && incomingDrawings.length === 0) return null;

  const transform = `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`;

  return (
    <svg
      className="absolute inset-0 z-[5] overflow-visible"
      style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
    >
      <defs>
        <linearGradient id="subprocessBadgeFill" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#14b8a6" />
          <stop offset="55%" stopColor="#0d9488" />
          <stop offset="100%" stopColor="#0f766e" />
        </linearGradient>
        <linearGradient id="subprocessIncomingBadgeFill" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f87171" />
          <stop offset="55%" stopColor="#dc2626" />
          <stop offset="100%" stopColor="#b91c1c" />
        </linearGradient>
        <filter id="subprocessBadgeGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="2" stdDeviation="2.4" floodColor="#0f766e" floodOpacity="0.35" />
        </filter>
        <filter id="subprocessIncomingBadgeGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="2" stdDeviation="2.4" floodColor="#b91c1c" floodOpacity="0.35" />
        </filter>
      </defs>
      <g style={{ transform, transformOrigin: '0 0' }}>
        {outgoingDrawings.map((item) => (
          <ProcessCapsule
            key={item.key}
            processId={item.processId}
            anchor={item.anchor}
            badge={item.badge}
            itemKey={item.key}
            gradientId="subprocessBadgeFill"
            glowId="subprocessBadgeGlow"
            stroke="#99f6e4"
            lineStroke="#0f766e"
            fromAbove={false}
            onClick={handleCapsuleClick}
          />
        ))}
        {incomingDrawings.map((item) => (
          <ProcessCapsule
            key={item.key}
            processId={item.processId}
            anchor={item.anchor}
            badge={item.badge}
            itemKey={item.key}
            gradientId="subprocessIncomingBadgeFill"
            glowId="subprocessIncomingBadgeGlow"
            stroke="#fecaca"
            lineStroke="#b91c1c"
            fromAbove
            onClick={handleCapsuleClick}
          />
        ))}
      </g>
    </svg>
  );
}
